import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, EnvelopeSimple } from "@phosphor-icons/react";

export default function Schedule() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [remindingId, setRemindingId] = useState(null);

  const sendReminder = async (o) => {
    setRemindingId(o.id);
    try {
      await api.post(`/schedule/orders/${o.id}/remind`);
      toast.success(`Reminder emailed to client for ${o.order_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
    } finally {
      setRemindingId(null);
    }
  };

  useEffect(() => {
    api.get("/schedule")
      .then((r) => setClients(r.data.clients || []))
      .catch(() => toast.error("Could not load schedule"))
      .finally(() => setLoading(false));
  }, []);

  const downloadPdf = (c) => {
    const doc = new jsPDF();
    const M = 16;                 // page margin
    const RIGHT = 194;            // right content edge
    const COL = { patient: M, product: 72, order: 150, qty: RIGHT };
    const BOTTOM = 280;
    const totalUnits = c.orders.reduce((s, o) => s + (o.items || []).reduce((a, it) => a + (Number(it.quantity) || 0), 0), 0);
    let y = 0;

    const drawPageHeader = () => {
      y = 18;
      doc.setFont("helvetica", "bold"); doc.setFontSize(18);
      doc.setTextColor(30, 127, 212);
      doc.text("Heliopolis Pharmaceuticals", M, y);
      doc.setTextColor(120); doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      doc.text("Undispatched Orders Schedule", M, (y += 6));
      doc.setTextColor(20); doc.setFont("helvetica", "bold"); doc.setFontSize(14);
      doc.text(c.client_name, M, (y += 12));
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(120);
      doc.text(`Generated ${fmtDate(new Date().toISOString())}`, M, (y += 6));
      doc.text(`${c.orders.length} order(s) · ${totalUnits} unit(s) pending dispatch`, RIGHT, y, { align: "right" });
      doc.setTextColor(20);
      y += 10;
      drawTableHeader();
    };

    const drawTableHeader = () => {
      doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(120);
      doc.text("PATIENT", COL.patient, y);
      doc.text("PRODUCT", COL.product, y);
      doc.text("ORDER", COL.order, y);
      doc.text("QTY", COL.qty, y, { align: "right" });
      doc.setDrawColor(210); doc.line(M, y + 2, RIGHT, y + 2);
      doc.setTextColor(20); doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      y += 8;
    };

    const ensureSpace = (needed) => {
      if (y + needed > BOTTOM) { doc.addPage(); drawPageHeader(); }
    };

    drawPageHeader();
    c.orders.forEach((o, oi) => {
      const items = o.items && o.items.length ? o.items : [{ name: "—", quantity: "" }];
      ensureSpace(items.length * 6 + 4);
      items.forEach((it, idx) => {
        if (y > BOTTOM) { doc.addPage(); drawPageHeader(); }
        if (idx === 0) {
          doc.setFont("helvetica", "bold");
          doc.text(String(o.patient_name || "—").slice(0, 32), COL.patient, y);
          doc.setTextColor(120); doc.setFontSize(9);
          doc.text(String(o.order_number || "").slice(0, 22), COL.order, y);
          doc.setTextColor(20); doc.setFontSize(10); doc.setFont("helvetica", "normal");
        }
        doc.text(String(it.name || "").slice(0, 48), COL.product, y);
        doc.text(String(it.quantity ?? ""), COL.qty, y, { align: "right" });
        y += 6;
      });
      // subtle separator between orders
      if (oi < c.orders.length - 1) {
        doc.setDrawColor(235); doc.line(M, y, RIGHT, y);
        y += 5;
      }
    });

    // footer page numbers
    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      doc.setFontSize(8); doc.setTextColor(150);
      doc.text(`Page ${p} of ${pages}`, RIGHT, 290, { align: "right" });
    }
    doc.save(`schedule-${c.client_name.replace(/\s+/g, "_")}.pdf`);
  };

  const q = search.trim().toLowerCase();
  const filtered = clients.filter((c) =>
    !q ||
    c.client_name.toLowerCase().includes(q) ||
    c.orders.some((o) => (o.patient_name || "").toLowerCase().includes(q) ||
      (o.items || []).some((it) => (it.name || "").toLowerCase().includes(q)))
  );

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <PageHeader title="Schedule" subtitle="Orders not yet sent to dispatch, grouped by client" />

      <div className="relative max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="schedule-search"
          placeholder="Search client, patient or product…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState title="Nothing to schedule" hint="All client orders have been sent to dispatch." />
      ) : (
        <div className="space-y-6">
          {filtered.map((c) => (
            <div key={c.client_id} data-testid={`schedule-client-${c.client_id}`} className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border bg-muted/40">
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold truncate">{c.client_name}</h3>
                  <p className="text-xs text-muted-foreground">{c.orders.length} order(s) pending dispatch</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm shrink-0"
                  onClick={() => downloadPdf(c)}
                  data-testid={`schedule-pdf-${c.client_id}`}
                >
                  <DownloadSimple size={16} className="mr-2" /> PDF
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-fixed sm:table-auto">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                      <th className="px-3 sm:px-5 py-2 font-medium w-[32%] sm:w-auto">Patient</th>
                      <th className="px-3 sm:px-5 py-2 font-medium w-[38%] sm:w-auto">Product</th>
                      <th className="px-3 sm:px-5 py-2 font-medium text-right w-[12%] sm:w-auto">Qty</th>
                      <th className="px-3 sm:px-5 py-2 font-medium hidden sm:table-cell">Order</th>
                      <th className="px-3 sm:px-5 py-2 font-medium text-right w-[18%] sm:w-auto">Reminder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.orders.flatMap((o) =>
                      (o.items || []).map((it, idx) => (
                        <tr key={`${o.order_number}-${idx}`} className="border-b border-border/50 last:border-0">
                          <td className="px-3 sm:px-5 py-2 break-words">{idx === 0 ? (o.patient_name || "—") : ""}{idx === 0 && <div className="sm:hidden text-xs text-muted-foreground">{o.order_number}</div>}</td>
                          <td className="px-3 sm:px-5 py-2 break-words">{it.name}</td>
                          <td className="px-3 sm:px-5 py-2 text-right tabular-nums">{it.quantity}</td>
                          <td className="px-3 sm:px-5 py-2 text-muted-foreground hidden sm:table-cell">{idx === 0 ? o.order_number : ""}</td>
                          <td className="px-3 sm:px-5 py-2 text-right">
                            {idx === 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-sm h-8 px-2 sm:px-3"
                                disabled={remindingId === o.id}
                                onClick={() => sendReminder(o)}
                                data-testid={`schedule-remind-${o.order_number}`}
                              >
                                <EnvelopeSimple size={14} className="sm:mr-1.5" />
                                <span className="hidden sm:inline">{remindingId === o.id ? "Sending…" : "Remind"}</span>
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
